<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useLocalization } from '@/composables/useLocalization'

const { __ } = useLocalization()

const props = defineProps({
  dataUrl: { type: String, required: true },
  saveUrl: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(true)
const saving = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const locales = ref({})
const buttons = ref({})
const audiences = ref({})
const shared = ref({ open_menu: { defaults: {}, labels: {} } })
const maxPerRow = ref(4)
const tab = ref('subscriber')
const editing = ref({ key: 'open_menu', audience: null })
const drag = ref(null)
const editorPanel = ref(null)

const audienceMeta = {
  subscriber: {
    title: __('Users with a subscription'),
    hint: __('Shown after registration is complete, to users with an active plan.'),
  },
  guest: {
    title: __('Users without a subscription'),
    hint: __('Shown after registration is complete, to users without an active plan.'),
  },
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

function applyPayload(data) {
  locales.value = data.locales || {}
  buttons.value = data.buttons || {}
  audiences.value = data.audiences || {}
  shared.value = data.shared || { open_menu: { defaults: {}, labels: {} } }
  maxPerRow.value = Number(data.maxPerRow || 4)
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const res = await fetch(props.dataUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error(res.statusText)
    applyPayload(await res.json())
  } catch (error) {
    errorMessage.value = error?.message || __('Failed to load the menu')
  } finally {
    loading.value = false
  }
}

async function save() {
  if (!props.canEdit) return
  saving.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const payload = { audiences: {}, shared: { open_menu: shared.value.open_menu?.labels || {} } }
    for (const [key, audience] of Object.entries(audiences.value)) {
      payload.audiences[key] = {
        rows: audience.rows || [],
        labels: audience.labels || {},
      }
    }
    const res = await fetch(props.saveUrl, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
      body: JSON.stringify(payload),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || res.statusText)
    applyPayload(data)
    successMessage.value = __('Menu saved. Users see it the next time they open the menu.')
  } catch (error) {
    errorMessage.value = error?.message || __('Could not save the menu')
  } finally {
    saving.value = false
  }
}

function caption(key) {
  return buttons.value[key]?.caption || key
}

function note(key) {
  return buttons.value[key]?.note || ''
}

function isRequired(key) {
  return Boolean(buttons.value[key]?.required)
}

function displayLabel(key, audienceKey) {
  const labels = audienceKey
    ? audiences.value[audienceKey]?.labels?.[key]
    : shared.value.open_menu?.labels
  const defaults = audienceKey
    ? buttons.value[key]?.defaults
    : shared.value.open_menu?.defaults
  const fa = labels?.fa || defaults?.fa
  return fa || caption(key)
}

function currentAudience() {
  return audiences.value[tab.value] || { rows: [], hidden: [], labels: {} }
}

function takeOut(audience, key) {
  audience.hidden = (audience.hidden || []).filter((item) => item !== key)
  audience.rows = (audience.rows || [])
    .map((row) => row.filter((item) => item !== key))
    .filter((row) => row.length > 0)
}

function removeFromRows(audienceKey, key) {
  const audience = audiences.value[audienceKey]
  if (!audience || isRequired(key)) return
  takeOut(audience, key)
  if (!audience.hidden.includes(key)) {
    audience.hidden = [...audience.hidden, key]
  }
}

function insertAt(audienceKey, key, rowIndex, colIndex) {
  const audience = audiences.value[audienceKey]
  if (!audience) return
  takeOut(audience, key)

  if (rowIndex === 'new' || rowIndex >= audience.rows.length) {
    audience.rows.push([key])
    return
  }

  const row = [...(audience.rows[rowIndex] || [])]
  if (row.length >= maxPerRow.value) {
    audience.rows.splice(rowIndex + 1, 0, [key])
    return
  }

  const at = Math.max(0, Math.min(colIndex, row.length))
  row.splice(at, 0, key)
  audience.rows[rowIndex] = row
}

function moveInRow(audienceKey, rowIndex, colIndex, step) {
  const audience = audiences.value[audienceKey]
  const row = audience?.rows?.[rowIndex]
  if (!row) return
  const next = colIndex + step
  if (next < 0 || next >= row.length) return
  const copy = [...row]
  const [item] = copy.splice(colIndex, 1)
  copy.splice(next, 0, item)
  audience.rows[rowIndex] = copy
}

function onDragStart(payload) {
  if (!props.canEdit) return
  drag.value = payload
}

function onDropAt(audienceKey, rowIndex, colIndex) {
  if (!props.canEdit || !drag.value || drag.value.audience !== audienceKey) return
  insertAt(audienceKey, drag.value.key, rowIndex, colIndex)
  drag.value = null
}

function onDropHidden(audienceKey) {
  if (!props.canEdit || !drag.value || drag.value.audience !== audienceKey) return
  removeFromRows(audienceKey, drag.value.key)
  drag.value = null
}

function openEditor(key, audienceKey) {
  editing.value = { key, audience: audienceKey }

  if (typeof window !== 'undefined' && window.innerWidth < 1280) {
    nextTick(() => editorPanel.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
}

const editorTitle = computed(() => {
  if (!editing.value) return ''
  return editing.value.key === 'open_menu' ? __('Open-menu button') : caption(editing.value.key)
})

const editorLabels = computed(() => {
  if (!editing.value) return {}
  if (editing.value.key === 'open_menu') {
    return shared.value.open_menu?.labels || {}
  }
  return audiences.value[editing.value.audience]?.labels?.[editing.value.key] || {}
})

function defaultLabel(locale) {
  if (!editing.value) return ''
  if (editing.value.key === 'open_menu') {
    return shared.value.open_menu?.defaults?.[locale] || ''
  }
  return buttons.value[editing.value.key]?.defaults?.[locale] || ''
}

function setEditorLabel(locale, value) {
  if (!editing.value) return
  if (editing.value.key === 'open_menu') {
    shared.value.open_menu.labels = { ...(shared.value.open_menu.labels || {}), [locale]: value }
    return
  }
  const audience = audiences.value[editing.value.audience]
  if (!audience) return
  audience.labels = audience.labels || {}
  audience.labels[editing.value.key] = {
    ...(audience.labels[editing.value.key] || {}),
    [locale]: value,
  }
}

function isEditing(key, audienceKey = null) {
  return editing.value?.key === key && editing.value?.audience === audienceKey
}

onMounted(load)
</script>

<template>
  <div class="ios-menu">
    <header class="ios-hero">
      <div class="ios-hero__copy">
        <p class="ios-eyebrow">Pointra</p>
        <h2 class="ios-title">{{ __('Reply keyboard') }}</h2>
        <p class="ios-subtitle">
          {{ __('Use the arrows to move a button inside its row. Drag it to another row, or hide it. Click the pencil to rename it.') }}
        </p>
      </div>
      <div class="ios-hero__actions">
        <button type="button" class="ios-btn ios-btn--ghost" :disabled="loading" @click="load">
          {{ __('Reload') }}
        </button>
        <button
          type="button"
          class="ios-btn ios-btn--primary"
          :disabled="!canEdit || saving || loading"
          @click="save"
        >
          {{ saving ? __('Saving…') : __('Save menu') }}
        </button>
      </div>
      <p v-if="errorMessage" class="ios-banner ios-banner--error">{{ errorMessage }}</p>
      <p v-if="successMessage" class="ios-banner ios-banner--ok">{{ successMessage }}</p>
    </header>

    <div v-if="loading" class="ios-loading">
      <span class="ios-spinner" />
      {{ __('Loading…') }}
    </div>

    <template v-else>
      <div class="ios-segment" role="tablist">
        <button
          v-for="(meta, key) in audienceMeta"
          :key="key"
          type="button"
          role="tab"
          class="ios-segment__item"
          :class="{ 'is-active': tab === key }"
          :aria-selected="tab === key"
          @click="tab = key"
        >
          {{ meta.title }}
        </button>
      </div>
      <p class="ios-hint">{{ audienceMeta[tab].hint }}</p>

      <div class="ios-layout">
        <div class="ios-main">
          <section class="ios-card">
            <div class="ios-card__head">
              <h3>{{ __('Open-menu button') }}</h3>
              <p>{{ __('Shown when the full menu is closed.') }}</p>
            </div>
            <button
              type="button"
              class="ios-chip"
              :class="{ 'is-selected': isEditing('open_menu') }"
              @click="openEditor('open_menu', null)"
            >
              <span class="ios-chip__label" dir="auto">{{ displayLabel('open_menu') }}</span>
              <span class="ios-chip__glyph" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none"><path d="M13.2 3.8a1.7 1.7 0 0 1 2.4 2.4L7.5 14.3 4 15l.7-3.5 8.5-7.7Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
              </span>
            </button>
          </section>

          <section class="ios-card">
            <div class="ios-card__head">
              <h3>{{ __('Keyboard') }}</h3>
              <p>{{ __('Use the arrows to move a button inside its row. Drag it to another row, or hide it. Click the pencil to rename it.') }}</p>
            </div>

            <div class="ios-keys">
              <div
                v-for="(row, rowIndex) in currentAudience().rows"
                :key="'row-' + rowIndex"
                class="ios-keys__row"
                dir="ltr"
                @dragover.prevent
                @drop.prevent="onDropAt(tab, rowIndex, row.length)"
              >
                <div
                  v-for="(key, colIndex) in row"
                  :key="key"
                  class="ios-key"
                  :class="{
                    'is-selected': isEditing(key, tab),
                    'is-dragging': drag?.key === key && drag?.audience === tab,
                  }"
                  :draggable="canEdit"
                  @dragstart="onDragStart({ audience: tab, key })"
                  @dragover.prevent
                  @drop.stop.prevent="onDropAt(tab, rowIndex, colIndex)"
                >
                  <div class="ios-key__chrome" dir="ltr">
                    <div class="ios-key__movers">
                      <button
                        type="button"
                        class="ios-icon-btn"
                        :disabled="!canEdit || colIndex === 0"
                        :title="__('Move left')"
                        @click="moveInRow(tab, rowIndex, colIndex, -1)"
                      >
                        <svg viewBox="0 0 16 16" fill="none"><path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                      <button
                        type="button"
                        class="ios-icon-btn"
                        :disabled="!canEdit || colIndex === row.length - 1"
                        :title="__('Move right')"
                        @click="moveInRow(tab, rowIndex, colIndex, 1)"
                      >
                        <svg viewBox="0 0 16 16" fill="none"><path d="m6 3 5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </div>
                    <div class="ios-key__tools">
                      <button
                        type="button"
                        class="ios-icon-btn ios-icon-btn--accent"
                        :title="__('Edit name')"
                        @click="openEditor(key, tab)"
                      >
                        <svg viewBox="0 0 16 16" fill="none"><path d="M10.8 2.7a1.3 1.3 0 0 1 1.8 1.8L5.6 11.5 3 12l.5-2.6 7.3-6.7Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>
                      </button>
                      <button
                        v-if="canEdit && !isRequired(key)"
                        type="button"
                        class="ios-icon-btn ios-icon-btn--danger"
                        :title="__('Hide')"
                        @click="removeFromRows(tab, key)"
                      >
                        <svg viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                      </button>
                    </div>
                  </div>
                  <div class="ios-key__label" dir="auto">{{ displayLabel(key, tab) }}</div>
                </div>
              </div>

              <div
                class="ios-dropzone"
                @dragover.prevent
                @drop.prevent="onDropAt(tab, 'new', 0)"
              >
                <span class="ios-dropzone__plus">+</span>
                {{ __('Drop here to start a new row') }}
              </div>
            </div>
          </section>

          <section class="ios-card">
            <div class="ios-card__head">
              <h3>{{ __('Hidden buttons') }}</h3>
              <p>{{ __('Drag a hidden button onto a row to show it.') }}</p>
            </div>
            <div
              class="ios-hidden"
              @dragover.prevent
              @drop.prevent="onDropHidden(tab)"
            >
              <button
                v-for="key in currentAudience().hidden"
                :key="key"
                type="button"
                class="ios-hidden__pill"
                :class="{ 'is-selected': isEditing(key, tab) }"
                :draggable="canEdit"
                @dragstart="onDragStart({ audience: tab, key })"
                @click="openEditor(key, tab)"
              >
                <span dir="auto">{{ displayLabel(key, tab) }}</span>
                <small v-if="note(key)">{{ note(key) }}</small>
              </button>
              <p v-if="!(currentAudience().hidden || []).length" class="ios-empty">
                {{ __('Every available button is already on the keyboard.') }}
              </p>
            </div>
          </section>
        </div>

        <aside ref="editorPanel" class="ios-editor">
          <div class="ios-editor__badge">{{ __('Button name') }}</div>
          <h3 class="ios-editor__title">{{ editorTitle }}</h3>
          <p class="ios-editor__hint">{{ __('Pick a button, then change the Persian and English labels.') }}</p>
          <p v-if="editing.key !== 'open_menu' && note(editing.key)" class="ios-editor__note">
            {{ note(editing.key) }}
          </p>

          <div class="ios-fields">
            <label v-for="(localeName, locale) in locales" :key="locale" class="ios-field">
              <span class="ios-field__label">{{ localeName }}</span>
              <input
                type="text"
                class="ios-field__input"
                :disabled="!canEdit"
                :maxlength="64"
                :placeholder="defaultLabel(locale)"
                :value="editorLabels[locale] || ''"
                @input="setEditorLabel(locale, $event.target.value)"
              >
            </label>
          </div>
          <p class="ios-editor__foot">{{ __('Leave a field empty to keep the default name.') }}</p>
        </aside>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ios-menu {
  --ios-bg: #0b0b0f;
  --ios-surface: rgba(28, 28, 33, 0.86);
  --ios-surface-2: rgba(44, 44, 50, 0.92);
  --ios-elevated: #2c2c32;
  --ios-border: rgba(255, 255, 255, 0.08);
  --ios-border-strong: rgba(255, 255, 255, 0.14);
  --ios-text: #f5f5f7;
  --ios-muted: #98989f;
  --ios-blue: #0a84ff;
  --ios-blue-soft: rgba(10, 132, 255, 0.18);
  --ios-red: #ff453a;
  --ios-green: #30d158;
  --ios-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
  --ios-radius: 22px;
  --ios-font: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
    "Segoe UI", "Helvetica Neue", Arial, sans-serif;

  min-width: 0;
  color: var(--ios-text);
  font-family: var(--ios-font);
  -webkit-font-smoothing: antialiased;
}

:global(.dark) .ios-menu,
.ios-menu {
  color-scheme: dark;
}

/* Light panel fallback when Nova is not in dark mode */
html:not(.dark) .ios-menu {
  --ios-bg: #f2f2f7;
  --ios-surface: rgba(255, 255, 255, 0.82);
  --ios-surface-2: #ffffff;
  --ios-elevated: #ffffff;
  --ios-border: rgba(0, 0, 0, 0.06);
  --ios-border-strong: rgba(0, 0, 0, 0.1);
  --ios-text: #1c1c1e;
  --ios-muted: #8e8e93;
  --ios-blue: #007aff;
  --ios-blue-soft: rgba(0, 122, 255, 0.12);
  --ios-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.ios-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.5rem 1.5rem 1.35rem;
  border-radius: calc(var(--ios-radius) + 4px);
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(10, 132, 255, 0.22), transparent 55%),
    radial-gradient(90% 70% at 0% 100%, rgba(94, 92, 230, 0.16), transparent 50%),
    var(--ios-surface);
  border: 1px solid var(--ios-border);
  box-shadow: var(--ios-shadow);
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
}

.ios-hero__copy {
  min-width: 0;
  flex: 1 1 16rem;
}

.ios-eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ios-blue);
}

.ios-title {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.ios-subtitle {
  margin: 0.45rem 0 0;
  max-width: 38rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--ios-muted);
}

.ios-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.ios-btn {
  appearance: none;
  border: 0;
  cursor: pointer;
  border-radius: 999px;
  padding: 0.65rem 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  transition: transform 0.15s ease, opacity 0.15s ease, background 0.15s ease;
}

.ios-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ios-btn:not(:disabled):active {
  transform: scale(0.97);
}

.ios-btn--ghost {
  background: var(--ios-blue-soft);
  color: var(--ios-blue);
}

.ios-btn--primary {
  background: linear-gradient(180deg, #3b9eff 0%, var(--ios-blue) 100%);
  color: #fff;
  box-shadow: 0 8px 20px rgba(10, 132, 255, 0.35);
}

.ios-banner {
  flex: 1 0 100%;
  margin: 0;
  padding: 0.65rem 0.9rem;
  border-radius: 14px;
  font-size: 0.85rem;
}

.ios-banner--error {
  background: rgba(255, 69, 58, 0.14);
  color: var(--ios-red);
}

.ios-banner--ok {
  background: rgba(48, 209, 88, 0.14);
  color: var(--ios-green);
}

.ios-loading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.25rem;
  color: var(--ios-muted);
  font-size: 0.9rem;
}

.ios-spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 2px solid rgba(10, 132, 255, 0.25);
  border-top-color: var(--ios-blue);
  animation: ios-spin 0.7s linear infinite;
}

@keyframes ios-spin {
  to { transform: rotate(360deg); }
}

.ios-segment {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem;
  margin-top: 1.25rem;
  padding: 0.25rem;
  border-radius: 14px;
  background: rgba(120, 120, 128, 0.18);
  max-width: 34rem;
}

.ios-segment__item {
  appearance: none;
  border: 0;
  cursor: pointer;
  border-radius: 11px;
  padding: 0.55rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ios-muted);
  background: transparent;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.ios-segment__item.is-active {
  color: var(--ios-text);
  background: var(--ios-surface-2);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
}

.ios-hint {
  margin: 0.65rem 0 0;
  font-size: 0.82rem;
  color: var(--ios-muted);
}

.ios-layout {
  display: grid;
  gap: 1.1rem;
  margin-top: 1.1rem;
}

@media (min-width: 1280px) {
  .ios-layout {
    grid-template-columns: minmax(0, 1fr) 22rem;
    align-items: start;
  }
}

.ios-main {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.ios-card {
  padding: 1.15rem 1.2rem 1.25rem;
  border-radius: var(--ios-radius);
  background: var(--ios-surface);
  border: 1px solid var(--ios-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.ios-card__head h3 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.ios-card__head p {
  margin: 0.3rem 0 0.95rem;
  font-size: 0.78rem;
  color: var(--ios-muted);
  line-height: 1.4;
}

.ios-chip {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  max-width: 100%;
  padding: 0.7rem 0.95rem;
  border-radius: 16px;
  border: 1px solid var(--ios-border-strong);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  color: var(--ios-text);
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}

html:not(.dark) .ios-chip {
  background: linear-gradient(180deg, #fff, #f7f7fa);
}

.ios-chip:active {
  transform: scale(0.98);
}

.ios-chip.is-selected {
  border-color: rgba(10, 132, 255, 0.65);
  box-shadow: 0 0 0 3px var(--ios-blue-soft);
}

.ios-chip__label {
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.ios-chip__glyph {
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  color: var(--ios-blue);
}

.ios-chip__glyph svg {
  width: 100%;
  height: 100%;
}

.ios-keys {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.55rem;
  border-radius: 18px;
  background: rgba(120, 120, 128, 0.12);
}

html:not(.dark) .ios-keys {
  background: rgba(120, 120, 128, 0.1);
}

.ios-keys__row {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.45rem;
  min-height: 3.4rem;
}

.ios-key {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: var(--ios-surface-2);
  border: 1px solid var(--ios-border);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  cursor: grab;
  transition: transform 0.12s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  overflow: hidden;
}

html:not(.dark) .ios-key {
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.ios-key:active {
  cursor: grabbing;
  transform: scale(0.985);
}

.ios-key.is-selected {
  border-color: rgba(10, 132, 255, 0.55);
  background: var(--ios-blue-soft);
  box-shadow: 0 0 0 3px var(--ios-blue-soft);
}

.ios-key.is-dragging {
  opacity: 0.55;
}

.ios-key__chrome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.15rem;
  padding: 0.3rem 0.3rem 0;
  opacity: 0.45;
  transition: opacity 0.15s ease;
}

.ios-key:hover .ios-key__chrome,
.ios-key.is-selected .ios-key__chrome,
.ios-key:focus-within .ios-key__chrome {
  opacity: 1;
}

.ios-key__movers,
.ios-key__tools {
  display: flex;
  align-items: center;
  gap: 0.05rem;
}

.ios-icon-btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--ios-muted);
  width: 1.45rem;
  height: 1.45rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.ios-icon-btn svg {
  width: 0.85rem;
  height: 0.85rem;
}

.ios-icon-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.ios-icon-btn:not(:disabled):hover {
  background: rgba(120, 120, 128, 0.18);
  color: var(--ios-text);
}

.ios-icon-btn--accent {
  color: var(--ios-blue);
}

.ios-icon-btn--danger {
  color: var(--ios-red);
}

.ios-key__label {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.65rem 0.75rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ios-dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 2.85rem;
  margin-top: 0.1rem;
  border-radius: 14px;
  border: 1.5px dashed rgba(255, 255, 255, 0.16);
  color: var(--ios-muted);
  font-size: 0.75rem;
  font-weight: 500;
  background: transparent;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

html:not(.dark) .ios-dropzone {
  border-color: rgba(0, 0, 0, 0.14);
}

.ios-dropzone:hover {
  border-color: rgba(10, 132, 255, 0.55);
  color: var(--ios-blue);
  background: var(--ios-blue-soft);
}

.ios-dropzone__plus {
  display: inline-flex;
  width: 1.2rem;
  height: 1.2rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--ios-blue-soft);
  color: var(--ios-blue);
  font-size: 0.95rem;
  line-height: 1;
}

.ios-hidden {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-height: 3rem;
}

.ios-hidden__pill {
  appearance: none;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0.65rem 0.9rem;
  border-radius: 16px;
  border: 1px solid var(--ios-border-strong);
  background: var(--ios-elevated);
  color: var(--ios-text);
  cursor: grab;
  text-align: start;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.ios-hidden__pill span {
  font-size: 0.86rem;
  font-weight: 600;
}

.ios-hidden__pill small {
  font-size: 0.68rem;
  color: var(--ios-muted);
}

.ios-hidden__pill.is-selected {
  border-color: rgba(10, 132, 255, 0.65);
  box-shadow: 0 0 0 3px var(--ios-blue-soft);
}

.ios-empty {
  margin: 0;
  font-size: 0.78rem;
  color: var(--ios-muted);
}

.ios-editor {
  position: relative;
  padding: 1.25rem 1.2rem 1.35rem;
  border-radius: var(--ios-radius);
  background:
    radial-gradient(80% 60% at 100% 0%, rgba(10, 132, 255, 0.16), transparent 60%),
    var(--ios-surface);
  border: 1px solid var(--ios-border);
  box-shadow: var(--ios-shadow);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  scroll-margin-top: 6rem;
}

@media (min-width: 1280px) {
  .ios-editor {
    position: sticky;
    top: 1rem;
  }
}

.ios-editor__badge {
  display: inline-flex;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ios-blue);
  background: var(--ios-blue-soft);
}

.ios-editor__title {
  margin: 0.7rem 0 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.ios-editor__hint,
.ios-editor__note,
.ios-editor__foot {
  margin: 0.4rem 0 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--ios-muted);
}

.ios-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.1rem;
}

.ios-field__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ios-muted);
}

.ios-field__input {
  width: 100%;
  border-radius: 14px;
  border: 1px solid var(--ios-border-strong);
  background: rgba(120, 120, 128, 0.12);
  color: var(--ios-text);
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  font-weight: 500;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.ios-field__input:focus {
  border-color: rgba(10, 132, 255, 0.7);
  box-shadow: 0 0 0 3px var(--ios-blue-soft);
  background: rgba(10, 132, 255, 0.06);
}

.ios-field__input:disabled {
  opacity: 0.5;
}

.ios-field__input::placeholder {
  color: var(--ios-muted);
  opacity: 0.75;
}
</style>
